
import { useState } from "react"
import { Link, useNavigate, useSearchParams } from "react-router"
import { authApi } from "@/lib/api/auth"
import { api } from "@/lib/api/client"
import { toast } from "sonner"
import { motion } from "motion/react"
import { Eye, EyeOff } from "lucide-react"

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get("token") || ""

  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)

  const inputClass = "w-full h-11 rounded-xl border border-zinc-200 bg-white px-4 pr-11 text-sm text-zinc-900 placeholder:text-zinc-400 outline-none focus:border-zinc-400 focus:ring-1 focus:ring-zinc-400 transition-all duration-150"
  const labelClass = "block text-sm font-medium text-zinc-700 mb-1.5"

  const validate = (): string | null => {
    if (newPassword.length < 8 || newPassword.length > 40) {
      return "Password must be between 8 and 40 characters."
    }
    if (newPassword !== confirmPassword) {
      return "Passwords do not match."
    }
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    if (!token) {
      setError("Reset token is missing. Please use the link from your email.")
      return
    }

    setIsSubmitting(true)
    try {
      const { accessToken } = await authApi.resetPassword({ token, newPassword })
      api.setToken(accessToken)
      toast.success("Password reset successfully!")
      navigate("/dashboard")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset password. The link may have expired.")
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-lg"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center size-14 rounded-2xl bg-zinc-900 mb-4">
              <span className="text-white font-bold text-lg" style={{ fontFamily: "var(--font-syne)" }}>
                eT<span className="text-emerald-400">X</span>
              </span>
            </div>
            <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Invalid reset link</h1>
            <p className="text-sm text-zinc-400 mt-2">This password reset link is invalid or has expired.</p>
          </div>
          <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6 space-y-4">
            <Link
              to="/forgot-password"
              className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium flex items-center justify-center hover:bg-black transition-colors duration-150"
            >
              Request a New Link
            </Link>
            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
              >
                Back to Sign In
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center size-14 rounded-2xl bg-zinc-900 mb-4">
            <span className="text-white font-bold text-lg" style={{ fontFamily: "var(--font-syne)" }}>
              eT<span className="text-emerald-400">X</span>
            </span>
          </div>
          <h1 className="text-2xl font-bold text-zinc-900 tracking-tight">Set a new password</h1>
          <p className="text-sm text-zinc-400 mt-2">Choose a strong password for your account.</p>
        </div>

        {/* Card */}
        <div className="rounded-2xl border border-zinc-200/60 bg-white shadow-sm p-6">
          {error && (
            <div className="mb-5 rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-600">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className={labelClass}>New Password</label>
              <div className="relative">
                <input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  className={inputClass}
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={40}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute inset-y-0 right-3 flex items-center"
                  aria-label={showNew ? "Hide password" : "Show password"}
                >
                  {showNew
                    ? <EyeOff className="size-4 text-zinc-400 hover:text-zinc-600 transition-colors" />
                    : <Eye className="size-4 text-zinc-400 hover:text-zinc-600 transition-colors" />}
                </button>
              </div>
              <p className="text-xs text-zinc-400 mt-1.5">8-40 characters</p>
            </div>

            <div>
              <label className={labelClass}>Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className={inputClass}
                  required
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={40}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute inset-y-0 right-3 flex items-center"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                >
                  {showConfirm
                    ? <EyeOff className="size-4 text-zinc-400 hover:text-zinc-600 transition-colors" />
                    : <Eye className="size-4 text-zinc-400 hover:text-zinc-600 transition-colors" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!newPassword || !confirmPassword || isSubmitting}
              className="w-full h-11 rounded-xl bg-zinc-900 text-white text-sm font-medium flex items-center justify-center gap-2 hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
            >
              {isSubmitting ? (
                <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                "Reset Password"
              )}
            </button>

            <div className="text-center">
              <Link
                to="/login"
                className="text-sm text-zinc-400 hover:text-zinc-700 transition-colors"
              >
                Back to Sign In
              </Link>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  )
}
